import React, { useEffect, useRef, useState } from "react"
import { useEditor } from "@layerhub-io/react"
import useDesignEditorContext from "~/hooks/useDesignEditorContext"
import { resolveSceneFrame } from "~/utils/sceneFrame"

/**
 * Scale a scene down to a display-friendly size for the preview modal.
 * Uses a higher max width than the footer thumbnails (1920 vs 400) so
 * the modal image looks crisp, but avoids rendering at full print
 * resolution (which can be 10000+ px for A3 formats).
 */
const MODAL_PREVIEW_MAX_WIDTH = 1920

const createModalPreviewScene = (scene: any, frame?: any): any => {
  const sceneFrame = resolveSceneFrame(scene, frame)
  if (sceneFrame.width <= MODAL_PREVIEW_MAX_WIDTH) {
    return { ...scene, frame: sceneFrame }
  }

  const scale = MODAL_PREVIEW_MAX_WIDTH / sceneFrame.width
  const newFrame = {
    width: Math.round(sceneFrame.width * scale),
    height: Math.round(sceneFrame.height * scale),
  }

  const scaledLayers = (scene.layers || []).map((layer: any) => {
    if (layer.type === 'Background') {
      return {
        ...layer,
        width: newFrame.width,
        height: newFrame.height,
        left: 0,
        top: 0,
        scaleX: 1,
        scaleY: 1,
      }
    }
    return {
      ...layer,
      left: (layer.left || 0) * scale,
      top: (layer.top || 0) * scale,
      scaleX: (layer.scaleX || 1) * scale,
      scaleY: (layer.scaleY || 1) * scale,
    }
  })

  return {
    ...scene,
    frame: newFrame,
    layers: scaledLayers,
  }
}

export default function () {
  const { scenes } = useDesignEditorContext()
  const [currentIndex, setCurrentIndex] = React.useState(0)
  const editor = useEditor()
  const [isLoading, setIsLoading] = useState(true)
  const [loadedPreviews, setLoadedPreviews] = useState<Record<string, string>>({})

  const hasStartedLoading = useRef(false)

  useEffect(() => {
    if (!editor || hasStartedLoading.current || scenes.length === 0) return
    hasStartedLoading.current = true

    const generateAllPreviews = async () => {
      setIsLoading(true)
      const previews: Record<string, string> = {}

      // Small delay to let any in-flight scene import settle so the canvas
      // is not in a transitional state when we export.
      await new Promise((resolve) => setTimeout(resolve, 150))

      // The scenes array from context holds stale JSON for the current scene
      // (only updated on scene switch). Export the live canvas state and
      // substitute it so the preview reflects unsaved edits.
      const liveScene = editor.scene.exportToJSON()

      // Guard: if the live export has significantly more layers than the
      // stored scene it claims to be, the canvas is likely in a mixed state
      // from concurrent imports — fall back to stored scene data.
      const storedMatch = scenes.find((s) => s.id === liveScene.id)
      const liveLayerCount = liveScene.layers?.length ?? 0
      const storedLayerCount = storedMatch?.layers?.length ?? 0
      const isLiveSceneClean =
        storedMatch && liveLayerCount <= storedLayerCount + 3

      try {
        for (let i = 0; i < scenes.length; i++) {
          const scene =
            scenes[i].id === liveScene.id && isLiveSceneClean
              ? { ...scenes[i], ...liveScene }
              : scenes[i]
          // Always render at a display-friendly resolution for the preview modal.
          // Do NOT use cached scene.preview — those are low-res thumbnails
          // scaled down to ~400px for the footer timeline.
          try {
            const targetFrame = scene.frame
            const previewScene = createModalPreviewScene(scene, targetFrame)
            const preview = await editor.renderer.render(previewScene)
            previews[scene.id] = preview as string
          } catch (err) {
            console.error(`Failed to render preview for scene ${i}:`, err)
          }
          setLoadedPreviews({ ...previews })
          await new Promise((resolve) => setTimeout(resolve, 50))
        }
      } catch (error) {
        console.error("Error generating previews:", error)
      } finally {
        setIsLoading(false)
      }
    }

    generateAllPreviews()
  }, [editor])

  const handleNext = () => {
    if (currentIndex < scenes.length - 1) setCurrentIndex(currentIndex + 1)
  }

  const handlePrevious = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1)
  }

  const currentPreview = scenes[currentIndex]
    ? loadedPreviews[scenes[currentIndex].id] || scenes[currentIndex].preview
    : null

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        background: "#f5f5f5",
      }}
    >
      {/* Scene counter */}
      <div
        style={{
          flexShrink: 0,
          textAlign: "center",
          padding: "8px 0 4px",
          color: "#666",
          fontSize: "13px",
          letterSpacing: "0.04em",
        }}
      >
        {scenes.length > 0 ? `${currentIndex + 1} / ${scenes.length}` : ""}
      </div>

      {/* Image area — takes all remaining space */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          padding: "8px 12px",
          minHeight: 0, // crucial: lets flex child shrink below natural height
        }}
      >
        {currentPreview ? (
          <img
            src={currentPreview}
            alt={`Scene ${currentIndex + 1}`}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              display: "block",
              borderRadius: "4px",
              boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
            }}
          />
        ) : (
          <div style={{ color: "#999", fontSize: "14px" }}>
            {isLoading
              ? `Loading preview ${currentIndex + 1} of ${scenes.length}…`
              : "No preview available"}
          </div>
        )}
      </div>

      {/* Navigation bar — always visible at bottom */}
      <div
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "16px",
          padding: "10px 0 14px",
        }}
      >
        <button
          onClick={handlePrevious}
          disabled={currentIndex === 0}
          style={{
            padding: "8px 24px",
            borderRadius: "6px",
            border: "1px solid #555",
            background: currentIndex === 0 ? "#e0e0e0" : "#555",
            color: currentIndex === 0 ? "#aaa" : "#fff",
            cursor: currentIndex === 0 ? "default" : "pointer",
            fontSize: "14px",
            transition: "background 0.15s",
          }}
        >
          ← Previous
        </button>
        <button
          onClick={handleNext}
          disabled={currentIndex === scenes.length - 1}
          style={{
            padding: "8px 24px",
            borderRadius: "6px",
            border: "1px solid #555",
            background: currentIndex === scenes.length - 1 ? "#e0e0e0" : "#555",
            color: currentIndex === scenes.length - 1 ? "#aaa" : "#fff",
            cursor: currentIndex === scenes.length - 1 ? "default" : "pointer",
            fontSize: "14px",
            transition: "background 0.15s",
          }}
        >
          Next →
        </button>
      </div>
    </div>
  )
}
